import type { GalleryItem } from '@/types'

import { useMemo, useState } from 'react'
import { ChevronLeft, ChevronRight, Expand } from 'lucide-react'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogTitle
} from '@/components/ui/dialog'
import { SmartImage } from '@/components/site/smart-image'
import { cms } from '@/lib/cms'
import { cn } from '@/lib/utils'

interface GalleryGridProps {
    items: Array<GalleryItem>
    showFilters?: boolean
    limit?: number
}

const ASPECTS = ['aspect-[4/5]', 'aspect-square', 'aspect-[3/4]', 'aspect-[4/5]', 'aspect-[5/6]', 'aspect-[3/4]']

export const GalleryGrid = ({ items, showFilters = true, limit }: GalleryGridProps) => {
    const [filter, setFilter] = useState('All')
    const [openIndex, setOpenIndex] = useState<number | null>(null)

    const categories = useMemo(() => ['All', ...Array.from(new Set(items.map(item => item.category)))], [items])
    const visible = useMemo(() => {
        const filtered = filter === 'All' ? items : items.filter(item => item.category === filter)

        return limit ? filtered.slice(0, limit) : filtered
    }, [filter, items, limit])

    const active = openIndex === null ? null : visible[openIndex] ?? null

    const step = (direction: number) => {
        if (openIndex === null || !visible.length) return

        setOpenIndex((openIndex + direction + visible.length) % visible.length)
    }

    return (
        <div>
            {showFilters && categories.length > 2 && (
                <div role='group' aria-label='Filter gallery' className='mb-10 flex flex-wrap justify-center gap-2'>
                    {categories.map(category => (
                        <button
                            key={category}
                            type='button'
                            onClick={() => setFilter(category)}
                            aria-pressed={filter === category}
                            className={cn(
                                'rounded-full border px-5 py-2 text-sm font-medium transition',
                                filter === category
                                    ? 'border-navy-900 bg-navy-900 text-cream-50'
                                    : 'border-border bg-card text-navy-900/80 hover:border-gold-400 hover:text-navy-900'
                            )}
                        >
                            {category}
                        </button>
                    ))}
                </div>
            )}

            <div className='columns-2 gap-4 md:columns-3 lg:gap-5'>
                {visible.map((item, index) => (
                    <figure key={item.id} className='mb-4 break-inside-avoid lg:mb-5'>
                        <button
                            type='button'
                            onClick={() => setOpenIndex(index)}
                            className={cn('group relative block w-full overflow-hidden rounded-2xl bg-muted text-left outline-none focus-visible:ring-3 focus-visible:ring-ring/60', ASPECTS[index % ASPECTS.length])}
                            aria-label={`Open ${item.title}`}
                        >
                            <SmartImage
                                src={item.image}
                                alt={item.image_alt}
                                sizes='(min-width: 1024px) 33vw, 50vw'
                                className='transition duration-700 group-hover:scale-105'
                            />
                            <span aria-hidden='true' className='absolute inset-0 bg-gradient-to-t from-navy-950/70 via-transparent to-transparent opacity-0 transition duration-300 group-hover:opacity-100' />
                            <span className='absolute top-3 right-3 flex size-9 items-center justify-center rounded-full bg-cream-50/90 text-navy-900 opacity-0 transition duration-300 group-hover:opacity-100'>
                                <Expand className='size-4' />
                            </span>
                        </button>
                        <figcaption className='mt-2.5 flex items-center justify-between gap-2 px-1 text-sm'>
                            <span data-mdlfy-cms={cms('gallery_items', item.id, 'title')} className='font-medium text-navy-900'>
                                {item.title}
                            </span>
                            <span data-mdlfy-cms={cms('gallery_items', item.id, 'category')} className='text-xs text-muted-foreground'>
                                {item.category}
                            </span>
                        </figcaption>
                    </figure>
                ))}
            </div>

            <Dialog open={active !== null} onOpenChange={open => { if (!open) setOpenIndex(null) }}>
                <DialogContent className='max-w-[calc(100%-1.5rem)] gap-0 overflow-hidden bg-navy-950 p-0 text-cream-50 ring-0 sm:max-w-4xl'>
                    {active && (
                        <>
                            <div className='relative aspect-[4/5] max-h-[78vh] w-full sm:aspect-[4/3]'>
                                <SmartImage src={active.image} alt={active.image_alt} sizes='(min-width: 1024px) 900px, 100vw' className='object-contain' />
                            </div>
                            <div className='flex items-center justify-between gap-4 px-5 py-4'>
                                <div>
                                    <DialogTitle data-mdlfy-cms={cms('gallery_items', active.id, 'title')} className='font-heading text-xl text-cream-50'>{active.title}</DialogTitle>
                                    <DialogDescription data-mdlfy-cms={cms('gallery_items', active.id, 'category')} className='text-xs text-cream-200/70'>{active.category}</DialogDescription>
                                </div>
                                <div className='flex gap-2'>
                                    <button type='button' onClick={() => step(-1)} aria-label='Previous image' className='flex size-10 items-center justify-center rounded-full border border-cream-50/20 transition hover:border-gold-300'>
                                        <ChevronLeft className='size-5' />
                                    </button>
                                    <button type='button' onClick={() => step(1)} aria-label='Next image' className='flex size-10 items-center justify-center rounded-full border border-cream-50/20 transition hover:border-gold-300'>
                                        <ChevronRight className='size-5' />
                                    </button>
                                </div>
                            </div>
                        </>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    )
}
